import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FileText, Upload, Download, Eye, CheckCircle2, Square,
  ChevronDown, ChevronUp, Trash2, FileImage, FileType, ShieldCheck, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABEL = {
  admin_report: { label: 'Report', tone: 'emerald' },
  admin_reference: { label: 'Reference', tone: 'blue' },
  client_evidence: { label: 'Evidence', tone: 'amber' },
  client_other: { label: 'Other', tone: 'slate' },
  reference: { label: 'Reference', tone: 'slate' },
};

const TONE_CLASS = {
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
};

function bytes(n) {
  if (n == null) return '—';
  if (n === 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * BookingDocuments — list / upload / preview / tick-to-review for a single booking.
 *
 * Props:
 *   bookingId: string
 *   token: string (JWT)
 *   role: 'client' | 'consultant' | 'admin'
 *   compact?: boolean    when true, collapsed by default
 *   onCountChange?: (n) => void
 */
export default function BookingDocuments({ bookingId, token, role, compact = true, onCountChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [collapsed, setCollapsed] = useState(compact);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const fileInputRef = useRef(null);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/documents`, { headers: auth });
      setDocs(res.data || []);
      onCountChange?.((res.data || []).length);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load documents.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, auth, onCountChange]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError('File exceeds 50 MB limit.');
      e.target.value = '';
      return;
    }
    const ok = ['application/pdf', 'image/jpeg', 'image/jpg'];
    if (!ok.includes(file.type)) {
      setError('Only JPEG or PDF files are allowed.');
      e.target.value = '';
      return;
    }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const docType = role === 'admin' ? 'admin_report' : 'client_evidence';
      await axios.post(
        `${API}/bookings/${bookingId}/documents?document_type=${docType}`,
        fd,
        { headers: { ...auth, 'Content-Type': 'multipart/form-data' } }
      );
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleMarkReviewed = async (doc, reviewed) => {
    setBusyId(doc.id);
    try {
      const res = await axios.post(
        `${API}/bookings/${bookingId}/documents/${doc.id}/mark-reviewed?reviewed=${reviewed}`,
        null,
        { headers: auth }
      );
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? res.data : d)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update review status.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.original_filename}"? This cannot be undone.`)) return;
    setBusyId(doc.id);
    try {
      await axios.delete(`${API}/bookings/${bookingId}/documents/${doc.id}`, { headers: auth });
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not remove document.');
    } finally {
      setBusyId(null);
    }
  };

  const downloadUrl = (doc, inline = false) =>
    `${API}/bookings/${bookingId}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}${inline ? '&inline=true' : ''}`;

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await axios.get(downloadUrl(doc), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || `document-${doc.id.slice(0, 8)}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Download failed.');
    } finally {
      setBusyId(null);
    }
  };

  const isAdmin = role === 'admin';
  const tickedField = isAdmin ? 'admin_reviewed_at' : 'client_reviewed_at';

  return (
    <section
      data-testid="booking-documents"
      data-booking-id={bookingId}
      className="border-t border-slate-200 bg-white"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        data-testid={`docs-toggle-${bookingId}`}
        className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-slate-800">Documents</span>
          <span
            data-testid="docs-count-badge"
            className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded-sm"
          >
            {docs.length}
          </span>
          {docs.some((d) => !d[tickedField]) && (
            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm border border-amber-200">
              {docs.filter((d) => !d[tickedField]).length} to review
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronUp className="w-4 h-4 text-slate-500" />}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.pdf,image/jpeg,application/pdf"
              onChange={handleUpload}
              className="hidden"
              data-testid={`docs-file-input-${bookingId}`}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid={`docs-upload-btn-${bookingId}`}
              className="px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-sm hover:bg-black disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : isAdmin ? 'Upload report / reference' : 'Upload supporting document'}
            </button>
            <span className="text-[11px] text-slate-500">
              JPEG or PDF only · Max 50 MB
            </span>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 py-4 text-center">Loading documents…</div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-slate-500 py-3 text-center bg-slate-50 border border-dashed border-slate-300 rounded-sm">
              No documents yet. {isAdmin
                ? 'Upload the official report or any reference PDFs/JPEGs for this client.'
                : 'Upload any supporting evidence (photos, plans, quotes) so the consulting team has the full picture.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-200 border border-slate-200 rounded-sm overflow-hidden">
              {docs.map((doc) => {
                const meta = TYPE_LABEL[doc.document_type] || TYPE_LABEL.reference;
                const ticked = !!doc[tickedField];
                const isImage = (doc.content_type || '').includes('image');
                const isMine = (role === 'admin' && doc.uploader_role === 'admin') ||
                  (role !== 'admin' && doc.uploader_role !== 'admin');
                return (
                  <li
                    key={doc.id}
                    data-testid={`docs-row-${doc.id}`}
                    className="px-3 py-2.5 grid grid-cols-12 gap-2 items-center hover:bg-slate-50"
                  >
                    <div className="col-span-12 md:col-span-5 min-w-0 flex items-center gap-2">
                      {isImage ? (
                        <FileImage className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      ) : (
                        <FileType className="w-4 h-4 text-rose-600 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate" title={doc.original_filename}>
                          {doc.original_filename}
                        </div>
                        {doc.description && (
                          <div className="text-[11px] text-slate-500 truncate" title={doc.description}>
                            {doc.description}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-span-6 md:col-span-2 flex items-center gap-1.5 text-[11px]">
                      <span className={`px-1.5 py-0.5 border rounded-sm font-semibold uppercase tracking-wider ${TONE_CLASS[meta.tone]}`}>
                        {meta.label}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 text-slate-500"
                        title={doc.uploader_role === 'admin' ? 'Uploaded by All Build Consult' : 'Uploaded by client'}
                      >
                        {doc.uploader_role === 'admin' ? <ShieldCheck className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      </span>
                    </div>

                    <div className="col-span-6 md:col-span-2 text-[11px] text-slate-500">
                      <div>{bytes(doc.size_bytes)}</div>
                      <div className="truncate" title={new Date(doc.uploaded_at).toLocaleString()}>
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                      <label
                        className="inline-flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 rounded-sm border border-slate-300 hover:bg-white text-[11px] font-medium text-slate-700"
                        title={isAdmin ? 'Tick when reviewed by admin' : 'Tick when you have reviewed this document'}
                        data-testid={`docs-review-toggle-${doc.id}`}
                      >
                        {busyId === doc.id ? (
                          <span className="w-3.5 h-3.5 animate-pulse">⋯</span>
                        ) : ticked ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Square className="w-3.5 h-3.5 text-slate-400" />
                        )}
                        <input
                          type="checkbox"
                          checked={ticked}
                          disabled={busyId === doc.id}
                          onChange={(e) => handleMarkReviewed(doc, e.target.checked)}
                          className="hidden"
                        />
                        {ticked ? 'Reviewed' : 'Mark reviewed'}
                      </label>
                      <a
                        href={downloadUrl(doc, true)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`docs-preview-${doc.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-700 border border-slate-300 rounded-sm hover:bg-white"
                      >
                        <Eye className="w-3.5 h-3.5" /> Preview
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        data-testid={`docs-download-${doc.id}`}
                        disabled={busyId === doc.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-white bg-slate-900 rounded-sm hover:bg-black disabled:opacity-50"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                      {(isAdmin || isMine) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(doc)}
                          data-testid={`docs-delete-${doc.id}`}
                          disabled={busyId === doc.id}
                          title="Remove document"
                          className="inline-flex items-center px-1.5 py-1 text-[11px] text-rose-700 border border-rose-200 rounded-sm hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
